import { passwordValidationSchema } from '@lili/lili-frontend-common';
import { z } from 'zod';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormHook, useSetError } from '@Hooks/index';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { OnboardingFormFields } from '@Types/index';
import { useBoundStore } from '@Store/useBoundStore';
import {
  AuthorizedUserOnboardingType,
  AuthorizedUserType,
} from '@Store/slices/authOnboarding/authOnboarding.types';
import { takeFullPageSnapshot } from '@Services/snapShot/snapshot.service';
import {
  useAuthUserCompleteOnboarding,
  useSetAuthorizedUserPassword,
} from '../api/setPassword.api';
import { useAuthorizedUserConsent } from '../api/useAuthorizedUserConsent';

export interface SetPasswordFormType {
  [OnboardingFormFields.PASSWORD]: string;
}

type AuthorizationTextKey =
  | 'accountantPersonalDetails.authorizationText'
  | 'adminPersonalDetails.authorizationTextManager'
  | 'adminPersonalDetails.authorizationText'
  | 'employeePersonalDetails.authorizationText';

const setPasswordFormScheme = z.object({
  [OnboardingFormFields.PASSWORD]: passwordValidationSchema,
});

export const useSetPasswordLogic = () => {
  const { t } = useTranslation('inviteTeamMember');
  const { navigateToNextPage } = useNavigationManager();
  const { mutateAsync: setPassword, isPending: isSettingPassword } = useSetAuthorizedUserPassword();
  const { mutateAsync: authUserCompleteOnboarding, isPending: isCompletingOnboarding } =
    useAuthUserCompleteOnboarding();
  const { mutateAsync: sendConsent, isPending: isSendingConsent } = useAuthorizedUserConsent();

  const onboardingType = useBoundStore.getState().getOnboardingType();
  const isKyc = onboardingType === AuthorizedUserOnboardingType.AUTHORIZEDUSER_KYC;
  const authUserType = useBoundStore.getState().getAuthUserType();

  let authorizationTitle: string;
  let authorizationTextKey: AuthorizationTextKey;

  if (authUserType === AuthorizedUserType.ACCOUNTANT) {
    authorizationTitle = t('accountantPersonalDetails.authorizationTitle');
    authorizationTextKey = 'accountantPersonalDetails.authorizationText';
  } else if (authUserType === AuthorizedUserType.MANAGER) {
    authorizationTitle = t('adminPersonalDetails.authorizationTitleManager');
    authorizationTextKey = 'adminPersonalDetails.authorizationTextManager';
  } else if (authUserType === AuthorizedUserType.EMPLOYEE) {
    authorizationTitle = t('employeePersonalDetails.authorizationTitle');
    authorizationTextKey = 'employeePersonalDetails.authorizationText';
  } else {
    authorizationTitle = t('adminPersonalDetails.authorizationTitle');
    authorizationTextKey = 'adminPersonalDetails.authorizationText';
  }

  const defaultValues: SetPasswordFormType = {
    [OnboardingFormFields.PASSWORD]: '',
  };

  const formMethods = useFormHook({
    formSchema: setPasswordFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'set-password-form',
  });

  const { isValid } = useFormState({ control: formMethods.control });

  const handleSubmit = async (data: SetPasswordFormType) => {
    try {
      await setPassword({ password: data[OnboardingFormFields.PASSWORD] });

      const consentFormData = await takeFullPageSnapshot({
        nodes: [
          document.querySelector('#page-content') as HTMLElement,
          document.querySelector('#page-footer') as HTMLElement,
        ],
      });
      if (consentFormData) {
        useBoundStore.getState().setConsentFormData(consentFormData);
      }

      await sendConsent();
      if (!isKyc) {
        await authUserCompleteOnboarding();
      }
      navigateToNextPage(RoutesMap.SET_PASSWORD);
    } catch (error) {
      console.error('Error setting password:', error);
    }
  };
  useSetError({ formMethods });

  return {
    formMethods,
    handleSubmit,
    isSettingPassword,
    isValid,
    isCompletingOnboarding,
    isSendingConsent,
    isKyc,
    authorizationTitle,
    authorizationTextKey,
  };
};
